import { TFunction } from 'i18next';
import { getAppConfig } from '@bts-web/core-app-config';
import { HTMLRenderer } from '@bts-web/core-features/common';
import {
  intlAssetValueFormatting,
  intlFiatValueFormatting,
} from '@bts-web/utils-formatting';
import {
  SavingsPlanTransactionItemType,
  StakingTransactionItemType,
} from '@bts-web/core-features/transactions';
import { TransactionCategory } from '@bts-web/data-layer/server';
import { TransactionDirection } from '../../transactions/types/transactions';
import { TransactionDetailsFooter } from '../TransactionDetailsFooter/TransactionDetailsFooter';
import { TransactionDetailsTitle } from '../TransactionDetailsTitle/TransactionDetailsTitle';
import { TransactionDetailsWrapper } from '../TransactionDetailsWrapper/TransactionDetailsWrapper';
import type { TransactionDetailsTransactionKeysResult } from '../utils';
import {
  CryptoActionViewQueryData,
  FiatTransactionViewQueryData,
  NonNullableChildrenTransaction,
  SwapOfferViewQueryData,
  TransactionViewQueryData,
} from '../types';
import { GenericErrorMessage } from '../../layouts/GenericErrorMessage/GenericErrorMessage';
import {
  isCryptoActionViewQueryNode,
  isFiatTransactionViewQueryNode,
  isStakingActionViewQueryNode,
  isStakingRewardActionViewQueryNode,
  isSwapViewQueryNode,
  isUnstakingActionViewQueryNode,
} from '../utils/transactionDetailsUtils';

export interface TransactionDetailsProps {
  translations: TransactionDetailsTransactionKeysResult;
  locale: string;
  currency?: string;
  transaction:
    | SwapOfferViewQueryData
    | SavingsPlanTransactionItemType
    | TransactionViewQueryData
    | CryptoActionViewQueryData
    | StakingTransactionItemType
    | FiatTransactionViewQueryData
    | null;
  translationUtil: TFunction;
  showActionButton?: boolean;
}

export const TransactionDetails = ({
  showActionButton = true,
  translations,
  transaction,
  locale,
  translationUtil,
}: TransactionDetailsProps) => {
  const { __typename } = transaction ?? {};

  const isSellType = __typename === 'SellTransaction';

  const isSwap = isSwapViewQueryNode(transaction);

  const isCryptoAction = isCryptoActionViewQueryNode(transaction);

  const { currency } = getAppConfig();

  const getSubtitle = () => {
    if (isFiatTransactionViewQueryNode(transaction)) {
      const formattedAmount = intlFiatValueFormatting(
        transaction.amount?.value ?? 0,
        {
          locale,
          currency,
          fractionDigits: transaction.amount?.precision ?? 2,
        },
      );

      return transaction.type === 'DEPOSIT'
        ? `${translations.received} ${formattedAmount}`
        : `${translations.sent} ${formattedAmount}`;
    }

    if (isSwap || isCryptoAction) {
      if (!transaction) return null;

      const childrenTransactions =
        transaction.childrenTransactions as NonNullable<
          SwapOfferViewQueryData['childrenTransactions']
        >;

      const outgoing = childrenTransactions.find(
        (tx) => tx.direction === TransactionDirection.OUTGOING,
      ) as NonNullableChildrenTransaction;

      const incoming = childrenTransactions.find(
        (tx) => tx.direction === TransactionDirection.INCOMING,
      ) as NonNullableChildrenTransaction;

      const outgoingAsset = outgoing.asset as NonNullable<
        NonNullableChildrenTransaction['asset']
      >;

      const incomingAsset = incoming.asset as NonNullable<
        NonNullableChildrenTransaction['asset']
      >;

      return (
        <HTMLRenderer>
          {translationUtil(
            isCryptoAction
              ? 'tx_crypto_action_item'
              : 'tx_details_swap_subtitle',
            {
              1: incomingAsset.symbol,
              0: outgoingAsset.symbol,
              interpolation: { escapeValue: false },
            },
          )}
        </HTMLRenderer>
      );
    }

    if (
      transaction?.__typename === 'GiveawayTransaction' &&
      'assetAmount' in transaction
    ) {
      const { assetAmount, asset } = transaction;

      const formattedAssetAmount = intlAssetValueFormatting(
        assetAmount?.value as string,
        {
          locale,
        },
      );

      return `${translations.received} ${formattedAssetAmount} ${asset?.symbol}`;
    }

    if (isStakingRewardActionViewQueryNode(transaction)) {
      const { fiatAmount, asset } = transaction;

      const formattedFiatAmount = intlFiatValueFormatting(
        fiatAmount?.value ?? 0,
        {
          locale,
          currency,
          fractionDigits: fiatAmount?.precision ?? 2,
        },
      );

      return `${translations.youReceived} ${formattedFiatAmount} ${asset?.symbol ?? ''}`;
    }

    if (
      (isStakingActionViewQueryNode(transaction) ||
        isUnstakingActionViewQueryNode(transaction)) &&
      'fiatAmount' in transaction
    ) {
      const { fiatAmount } = transaction;

      const formattedFiatAmount = intlFiatValueFormatting(
        fiatAmount?.value ?? 0,
        {
          locale,
          currency,
          fractionDigits: fiatAmount?.precision ?? 2,
        },
      );

      return `${isUnstakingActionViewQueryNode(transaction) ? translations.unstaked : translations.staked} ${formattedFiatAmount}`;
    }

    const { fiatAmount, asset } = transaction || {};

    const formattedFiatAmount = intlFiatValueFormatting(
      fiatAmount?.value ?? 0,
      {
        locale,
        currency,
        fractionDigits: fiatAmount?.precision ?? 2,
      },
    );

    const subtitleByCategory: Partial<Record<TransactionCategory, string>> = {
      TRADE: isSellType ? translations.typeSold : translations.typeBought,
      NON_USER_INITIATED: `${translations.received} ${asset?.name ?? ''}`,
      CORPORATE_ACTION: translations.youReceived,
    };

    if (transaction && 'category' in transaction) {
      const subtitleAction =
        subtitleByCategory[transaction.category as TransactionCategory] ??
        (isSellType ? translations.typeSold : translations.typeBought);

      return fiatAmount?.value && parseFloat(fiatAmount.value)
        ? `${subtitleAction} ${formattedFiatAmount}`
        : null;
    }

    return `${translationUtil('tx_savings_plan_item', { 0: formattedFiatAmount })}`;
  };

  if (transaction === null) {
    return <GenericErrorMessage />;
  }

  return (
    <>
      <TransactionDetailsTitle
        title={translations.details}
        subtitle={getSubtitle()}
      />

      <TransactionDetailsWrapper
        transaction={transaction}
        translations={translations}
        locale={locale}
        currency={currency}
      />

      <TransactionDetailsFooter
        transaction={transaction}
        showActionButton={showActionButton}
        translations={translations}
      />
    </>
  );
};
